import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Calendar,
  Clock,
  Users,
  Check,
  X,
  ArrowRightLeft,
  StickyNote,
  Send,
  Phone,
  Mail,
  Ban,
} from "lucide-react";
import BookingStatusBadge from "../../components/BookingStatusBadge";
import { useFormatDate } from "../../utils/dateFormat";

/**
 * Self-contained booking row used on both `/owner/bookings` and the calendar
 * day-detail panel. Each row owns its own dialog state (propose, cancel,
 * notes) so siblings don't fight over a single open prop. After any
 * mutating call, `onChanged()` fires so the parent can refetch its list and
 * the row remounts with fresh state.
 *
 * `cancelReasons` is preferred to be passed in once by the parent (so we
 * don't fetch /cancellation-reasons N times), but the row falls back to
 * fetching once if absent.
 */
export default function OwnerBookingRow({ booking, onChanged, cancelReasons: cancelReasonsProp }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const fmt = useFormatDate();

  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeForm, setProposeForm] = useState({ date: "", time: "", message: "" });
  const [proposing, setProposing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReasonId, setCancelReasonId] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelReasons, setCancelReasons] = useState(cancelReasonsProp || []);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);

  useEffect(() => {
    if (cancelReasonsProp) return;
    axios.get(`${API}/cancellation-reasons`).then((r) => setCancelReasons(r.data)).catch(() => {});
  }, [cancelReasonsProp]);

  const handleStatusChange = async (newStatus, message = "") => {
    try {
      await axios.put(`${API}/bookings/${booking.booking_id}/status`, {
        status: newStatus,
        venue_response_message: message || undefined,
      });
      toast.success(t("owner:toast_booking_updated"));
      onChanged?.();
    } catch {
      toast.error(t("owner:toast_update_failed"));
    }
  };

  const handlePropose = async () => {
    if (!proposeForm.date || !proposeForm.time) {
      toast.error(t("owner:toast_select_date_time"));
      return;
    }
    setProposing(true);
    try {
      await axios.put(`${API}/owner/bookings/${booking.booking_id}/propose-time`, {
        proposed_date: proposeForm.date,
        proposed_time: proposeForm.time,
        message: proposeForm.message || undefined,
      });
      toast.success(t("owner:toast_alt_proposed"));
      setProposeOpen(false);
      setProposeForm({ date: "", time: "", message: "" });
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("owner:toast_propose_failed"));
    } finally {
      setProposing(false);
    }
  };

  const handleCancel = async () => {
    let finalReason = cancelReason.trim();
    if (cancelReasonId) {
      const preset = cancelReasons.find((r) => r.reason_id === cancelReasonId);
      if (preset) {
        const presetLabel = (lang === "el" && preset.label_el) || preset.label_en;
        finalReason = finalReason ? `${presetLabel} — ${finalReason}` : presetLabel;
      }
    }
    if (!finalReason) {
      toast.error(t("owner:cancel_reason_required", { defaultValue: "Please select or enter a reason" }));
      return;
    }
    setCancelling(true);
    try {
      await axios.put(`${API}/bookings/${booking.booking_id}/status`, {
        status: "cancelled_by_venue",
        cancellation_reason: finalReason,
      });
      toast.success(t("owner:booking_cancelled", { defaultValue: "Booking cancelled" }));
      setCancelOpen(false);
      setCancelReason("");
      setCancelReasonId("");
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("owner:toast_cancel_failed"));
    } finally {
      setCancelling(false);
    }
  };

  const fetchNotes = async () => {
    setNotesLoading(true);
    try {
      const { data } = await axios.get(`${API}/owner/bookings/${booking.booking_id}/notes`);
      setNotes(data);
    } catch {
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/owner/bookings/${booking.booking_id}/notes`, { content: noteText.trim() });
      setNoteText("");
      fetchNotes();
      toast.success(t("owner:toast_note_added"));
    } catch {
      toast.error(t("owner:toast_note_failed"));
    }
  };

  const isTerminal = ["cancelled_by_customer", "cancelled_by_venue", "canceled_by_user", "completed", "rejected"].includes(booking.status);

  return (
    <div
      className="bg-card border border-border rounded-xl p-5"
      data-testid={`owner-booking-${booking.booking_id}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-medium text-foreground">{booking.contact_name}</h3>
            <BookingStatusBadge status={booking.status} />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {fmt(booking.booking_date, "EEE, MMM d")}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {booking.booking_time}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {t("booking:guests_count", { count: booking.guest_count })}
            </span>
            {booking.contact_phone && (
              <a
                href={`tel:${booking.contact_phone}`}
                className="flex items-center gap-1 hover:text-foreground"
                data-testid={`booking-phone-${booking.booking_id}`}
              >
                <Phone className="h-4 w-4" />
                {booking.contact_phone}
              </a>
            )}
            {booking.contact_email && (
              <a
                href={`mailto:${booking.contact_email}`}
                className="flex items-center gap-1 hover:text-foreground"
                data-testid={`booking-email-${booking.booking_id}`}
              >
                <Mail className="h-4 w-4" />
                {booking.contact_email}
              </a>
            )}
          </div>
          {booking.special_request && (
            <p className="text-sm text-muted-foreground mt-2 italic">"{booking.special_request}"</p>
          )}
          {booking.cancellation_reason && (
            <div
              className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700"
              data-testid={`booking-cancel-reason-${booking.booking_id}`}
            >
              <strong>
                {booking.cancelled_by === "venue"
                  ? t("owner:cancelled_by_venue_label", { defaultValue: "Cancelled by venue" })
                  : t("owner:cancelled_by_customer_label", { defaultValue: "Cancelled by customer" })}
                :
              </strong>{" "}
              {booking.cancellation_reason}
            </div>
          )}
          {booking.status === "change_proposed" && booking.proposed_new_date && (
            <div className="mt-2 p-2 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 shrink-0" />
              {t("owner:proposed_label", { defaultValue: "Proposed" })}: {booking.proposed_new_date} {t("customer_dashboard:at", { defaultValue: "at" })} {booking.proposed_new_time}
              {booking.proposal_message && <span className="text-blue-500 ml-1">- {booking.proposal_message}</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {booking.status === "pending" && (
            <>
              <Button size="sm" onClick={() => handleStatusChange("confirmed")} data-testid={`confirm-booking-${booking.booking_id}`}>
                <Check className="h-4 w-4 mr-1" />
                {t("owner:confirm")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleStatusChange("rejected")} data-testid={`reject-booking-${booking.booking_id}`}>
                <X className="h-4 w-4 mr-1" />
                {t("owner:reject")}
              </Button>
            </>
          )}
          {(booking.status === "pending" || booking.status === "confirmed") && (
            <Dialog open={proposeOpen} onOpenChange={(open) => { setProposeOpen(open); if (!open) setProposeForm({ date: "", time: "", message: "" }); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid={`propose-time-btn-${booking.booking_id}`}>
                  <ArrowRightLeft className="h-4 w-4 mr-1" />
                  {t("owner:propose_time")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("owner:propose_time")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label>{t("common:date")}</Label>
                    <Input type="date" value={proposeForm.date} onChange={(e) => setProposeForm({ ...proposeForm, date: e.target.value })} className="mt-1" data-testid="propose-date-input" />
                  </div>
                  <div>
                    <Label>{t("common:time")}</Label>
                    <Input type="time" value={proposeForm.time} onChange={(e) => setProposeForm({ ...proposeForm, time: e.target.value })} className="mt-1" data-testid="propose-time-input" />
                  </div>
                  <div>
                    <Label>{t("common:message")} ({t("common:optional")})</Label>
                    <Textarea placeholder={t("owner:propose_reason_placeholder")} value={proposeForm.message} onChange={(e) => setProposeForm({ ...proposeForm, message: e.target.value })} className="mt-1" data-testid="propose-message-input" />
                  </div>
                  <Button onClick={handlePropose} disabled={proposing} className="w-full" data-testid="propose-submit-btn">
                    {proposing ? t("billing:processing") : t("owner:propose_time")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {booking.status === "confirmed" && (
            <Button size="sm" variant="outline" onClick={() => handleStatusChange("completed")}>
              {t("owner:mark_completed")}
            </Button>
          )}
          {!isTerminal && (
            <Dialog open={cancelOpen} onOpenChange={(open) => { setCancelOpen(open); if (!open) setCancelReason(""); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" data-testid={`cancel-booking-${booking.booking_id}`}>
                  <Ban className="h-4 w-4 mr-1" />
                  {t("owner:cancel_booking", { defaultValue: "Cancel" })}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {t("owner:cancel_booking_title", { defaultValue: "Cancel booking" })} · {booking.contact_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <p className="text-sm text-muted-foreground">
                    {t("owner:cancel_booking_hint", { defaultValue: "The customer will be notified with the reason you select below." })}
                  </p>
                  {cancelReasons.length > 0 && (
                    <div>
                      <Label>{t("owner:cancel_reason_preset", { defaultValue: "Select a reason" })}</Label>
                      <Select value={cancelReasonId} onValueChange={setCancelReasonId}>
                        <SelectTrigger className="mt-1" data-testid="cancel-reason-select">
                          <SelectValue placeholder={t("owner:cancel_reason_placeholder", { defaultValue: "Pick a common reason…" })} />
                        </SelectTrigger>
                        <SelectContent>
                          {cancelReasons.map((r) => (
                            <SelectItem key={r.reason_id} value={r.reason_id}>
                              {(lang === "el" && r.label_el) || r.label_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>{t("owner:cancel_reason_note", { defaultValue: "Additional note (optional)" })}</Label>
                    <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder={t("owner:cancel_reason_note_placeholder", { defaultValue: "Extra context for the customer…" })} className="mt-1" data-testid="cancel-reason-input" />
                  </div>
                  <Button onClick={handleCancel} disabled={cancelling || (!cancelReasonId && !cancelReason.trim())} className="w-full" variant="destructive" data-testid="cancel-submit-btn">
                    {cancelling ? t("billing:processing", { defaultValue: "Processing…" }) : t("owner:confirm_cancel", { defaultValue: "Cancel booking" })}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={notesOpen} onOpenChange={(open) => { setNotesOpen(open); if (open) fetchNotes(); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="ghost" data-testid={`notes-btn-${booking.booking_id}`}>
                <StickyNote className="h-4 w-4 mr-1" />
                {t("owner:notes")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("owner:notes")} - {booking.contact_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2 max-h-80 overflow-y-auto">
                {notesLoading ? (
                  <div className="animate-pulse bg-muted h-20 rounded-lg" />
                ) : notes.length > 0 ? (
                  notes.map((note) => (
                    <div key={note.note_id} className="p-3 bg-muted/50 rounded-lg" data-testid={`note-${note.note_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{note.author_name}</span>
                        <span className="text-xs text-muted-foreground">{note.created_at?.split("T")[0]}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{note.content}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">{t("owner:notes_empty")}</p>
                )}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t">
                <Input placeholder={t("owner:add_note") + "..."} value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddNote()} data-testid="note-input" />
                <Button size="sm" onClick={handleAddNote} disabled={!noteText.trim()} data-testid="note-submit-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
